/* global maplibregl */
import { h, render, Fragment } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import htm from "htm";

const html = htm.bind(h);

function kmFromMeters(m) {
  if (!Number.isFinite(m)) return "";
  return (m / 1000).toFixed(1);
}

function haversineMeters(aLat, aLng, bLat, bLng) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);

  const hVal =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

  return 2 * R * Math.asin(Math.sqrt(hVal));
}

function normalizeStore(s) {
  return {
    id: s.store_id || "",
    name: s.name || "",
    country: s.country || "",
    city: s.region || "",
    address: s.address || "",
    phone: s.phone_number || "",
    lat: parseFloat(s.latitude) || 0,
    lng: parseFloat(s.longitude) || 0,
    google_maps_link: s.google_maps_link || "",
    image: s._image_url || "",
    image_all: s._image_all_urls || [],
    services: s.services || [],
    service_descriptions: s.service_descriptions || [],
    business_hours: s.business_hours || [],
    is_open: Boolean(s.is_open),
    store_description: s.store_description || "",
  };
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const fmt12 = (h, m) => `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "pm" : "am"}`;


function getStoreStatus(businessHours) {
  const now = new Date();
  const todayIdx = now.getDay();
  const entry =
    businessHours?.find((h) => h.startsWith(DAY_NAMES[todayIdx])) ??
    businessHours?.[todayIdx];
  const m = entry?.match(/(\d{1,2}):(\d{2})\s*[-\u2013]\s*(\d{1,2}):(\d{2})/);
  if (!m) return { isOpen: false, label: entry === undefined ? "" : "Closed today" };
  const [oH, oM, cH, cM] = [+m[1], +m[2], +m[3], +m[4]];
  const mins = now.getHours() * 60 + now.getMinutes();
  if (mins >= oH * 60 + oM && mins < cH * 60 + cM) return { isOpen: true, label: `Closes at ${fmt12(cH, cM)}` };
  if (mins < oH * 60 + oM) return { isOpen: false, label: `Opens at ${fmt12(oH, oM)}` };
  return { isOpen: false, label: "Closed" };
}

function uniqSorted(values) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

function safeJsonParse(str) {
  try {
    return JSON.parse(str);
  } catch (e) {
    console.warn("StoreLocator: failed to parse JSON", e);
    return null;
  }
}

function useGeolocation() {
  const [location, setLocation] = useState(null);

  // Request the browser's one-shot geolocation on mount
  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        setLocation(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 6000,
        maximumAge: 600000,
      }
    );
  }, []);

  return location;
}

function useMapLibre({ containerRef, styleUrl, defaultLat, defaultLng }) {
  const mapRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize MapLibre map once the container is ready; polls until the library loads
  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;

    function tryInit() {
      if (cancelled) return;
      if (!window.maplibregl || !maplibregl.Map) {
        window.setTimeout(tryInit, 50);
        return;
      }

      if (mapRef.current) return;

      mapRef.current = new maplibregl.Map({
        container: containerRef.current,
        style: styleUrl,
        center: [defaultLng, defaultLat],
        zoom: 5,
        attributionControl: false,
      });

      mapRef.current.once("load", () => { if (!cancelled) setMapLoaded(true); });

      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => { mapRef.current?.resize(); });
        ro.observe(containerRef.current);
        resizeObserverRef.current = ro;
      }
    }

    tryInit();

    return () => {
      cancelled = true;
      setMapLoaded(false);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [containerRef, styleUrl, defaultLat, defaultLng]);

  return { mapRef, mapLoaded };
}

function StoreCard({ store, onSelect }) {
  return html`
    <button type="button" class="store-locator__card" onClick=${() => onSelect(store.id)}>

      <div>
        <div class="store-locator__card-distance">
          <p class="store-locator__card-title">${store.name}</p>
          ${Number.isFinite(store.distance_m) ? `${kmFromMeters(store.distance_m)} km ›` : ""}
        </div>
        <div class="store-locator__card-address">${store.address}</div>
        <div class="store-locator__tags">
          ${(store.services || []).slice(0, 4).map((t) => html`<span class="store-locator__tag">${t}</span>`)}
        </div>
        ${(() => {
          const { isOpen, label } = getStoreStatus(store.business_hours);
          return html`<div class=${`store-locator__status ${isOpen ? "" : "store-locator__status--closed"}`}>
            <span>${isOpen ? "Open" : "Closed"}</span>
            ${label ? html`<span class="store-locator__status-label"><span class="store-locator__status-sep" aria-hidden="true"></span>${label}</span>` : null}
          </div>`;
        })()}

      </div>
            ${store.image
        ? html`<img class="store-locator__thumb" alt="" loading="lazy" src=${store.image} />`
        : null}
    </button>
  `;
}

const SLIDE_DURATION = 7000;

function DetailOverlay({ store, onClose }) {
  const images = store?.image_all?.length ? store.image_all : store?.image ? [store.image] : [];
  const [idx, setIdx] = useState(0);
  const [restartKey, setRestartKey] = useState(0);
  const prevIdxRef = useRef(0);

  useEffect(() => { prevIdxRef.current = idx; });

  // Reset slider to first image whenever the selected store changes
  useEffect(() => { setIdx(0); setRestartKey(0); prevIdxRef.current = 0; }, [store?.id]);

  // Auto-advance the slider on an interval; resets when restartKey changes (manual nav)
  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), SLIDE_DURATION);
    return () => clearInterval(t);
  }, [store?.id, images.length, restartKey]);

  const goTo = (i) => { setIdx((i + images.length) % images.length); setRestartKey((k) => k + 1); };
  const multi = images.length > 1;
  const cursorRef = useRef(null);

  const updateCursor = (e, dir) => {
    if (!cursorRef.current) return;
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    cursorRef.current.style.left = `${e.clientX - rect.left}px`;
    cursorRef.current.style.top = `${e.clientY - rect.top}px`;
    cursorRef.current.textContent = dir === "prev" ? "‹" : "›";
    cursorRef.current.style.opacity = "1";
  };
  const hideCursor = () => { if (cursorRef.current) cursorRef.current.style.opacity = "0"; };

  return html`
    <div class="store-locator__detail" aria-hidden=${store ? "false" : "true"}>
      <div class="store-locator__slide-wrap">
        ${prevIdxRef.current !== idx && images[prevIdxRef.current] ? html`
          <img
            key=${`out-${idx}`}
            class="store-locator__detail-photo store-locator__detail-photo--out"
            alt=""
            src=${images[prevIdxRef.current]}
          />
        ` : null}
        <img
          key=${`in-${idx}`}
          class="store-locator__detail-photo store-locator__detail-photo--in"
          alt=""
          loading="eager"
          width="1200"
          height="800"
          src=${images[idx] || ""}
        />
        ${multi ? html`
          <button
            class="store-locator__slide-prev"
            aria-label="Previous image"
            onClick=${() => goTo(idx - 1)}
            onMouseMove=${(e) => updateCursor(e, "prev")}
            onMouseLeave=${hideCursor}
          ></button>
          <button
            class="store-locator__slide-next"
            aria-label="Next image"
            onClick=${() => goTo(idx + 1)}
            onMouseMove=${(e) => updateCursor(e, "next")}
            onMouseLeave=${hideCursor}
          ></button>
          <div ref=${cursorRef} class="store-locator__slide-cursor" style="opacity:0"></div>
          <div class="store-locator__slide-bars">
            ${images.map((_, i) => html`
              <div class=${`store-locator__slide-bar ${i < idx ? "done" : i === idx ? "active" : ""}`}>
                ${i === idx ? html`<span key=${`${idx}-${restartKey}`} class="store-locator__slide-bar-fill"></span>` : null}
              </div>
            `)}
          </div>
        ` : null}
      </div>
      
      <div class="store-locator__detail-top">
        <button
          class="store-locator__detail-close"
          type="button"
          aria-label="Close"
          onClick=${onClose}
        >
          <span class="store-locator__detail-close__back">
            <img 
              src="https://cdn.shopify.com/s/files/1/0449/9433/0781/files/arrow-left.png?v=1741062715" 
              alt="back arrow" 
              width="12px" 
              height="12px" 
              style="vertical-align:middle;margin-right:4px;" 
            /> Back</span>
          <span class="store-locator__detail-close__x">✕</span>
        </button>
        <div>
        <h3 class="store-locator__detail-title">${store ? store.name : ""}</h3>
        <div class="store-locator__tags">
          ${(store?.services || []).map((t) => html`<span class="store-locator__tag">${t}</span>`)}
        </div>
                 ${(() => {
          const { isOpen, label } = getStoreStatus(store?.business_hours);
          return html`<div class=${`store-locator__status ${isOpen ? "" : "store-locator__status--closed"}`}>
            <span>${isOpen ? "Open" : "Closed"}</span>
            ${label ? html`<span class="store-locator__status-label"><span class="store-locator__status-sep" aria-hidden="true"></span>${label}</span>` : null}
          </div>`;
        })()}
        </div>
      </div>
      <div class="store-locator__detail-bottom">
        <div class="store-locator__detail-address">${store ? store.address : ""}</div>
        <div class="store-locator__detail-phone">TEL: ${store ? store.phone : ""}</div>
        <div class="store-locator__detail-actions">
          ${store?.google_maps_link
            ? html`<a
                class="store-locator__directions"
                href=${store.google_maps_link}
                target="_blank"
                rel="noopener"
              >Wegbeschreibung</a>`
            : null}
        </div>
        <div class="store-locator__services">
        <p class="store-locator__services-title">Beschreibung des Shops</p> <p class="store-locator__service-desc"> ${store?.store_description || ""} </p>
          ${store?.services && store.services.length > 0 && html`<p class="store-locator__services-title">DIENSTLEISTUNGEN</p>`}
          <div>
            ${(store?.services || []).map((name, idx) =>
              html`<div class="store-locator__service-item">
                <div class="store-locator__service-name">${name}</div>
                <div class="store-locator__service-desc">${store?.service_descriptions?.[idx] || ""}</div>
              </div>`
            )}
          </div>
          <div class="store-locator__detail-hours">
            <p class="store-locator__services-title">ÖFFNUNGSZEITEN</p>
            ${(store?.business_hours || []).map((h) => html`<div>${h}</div>`)}
          </div>
        </div>
      </div>
    </div>
  `;
}

function App({ root }) {
  const dataEl = root.querySelector("[data-store-locator-data]");
  const data = useMemo(() => {
    const parsed = safeJsonParse(dataEl?.textContent || "{}");
    const stores = parsed && Array.isArray(parsed.stores) ? parsed.stores : [];
    const imageUrls = parsed?.image_urls || {};

    return stores.map((s) => ({
      ...s,
      _image_url: imageUrls[s.store_id]?.main || "",
      _image_all_urls: imageUrls[s.store_id]?.all || [],
    }));
  }, [dataEl]);

  const storesBase = useMemo(() => {
    return data.map(normalizeStore).filter((s) => s.lat !== 0 || s.lng !== 0);
  }, [data]);

  const userLoc = useGeolocation();

  // Measure sticky header + banner combined height and expose it as --sl-nav-height
  useEffect(() => {
    const update = () => {
      let maxBottom = 0;
      document.querySelectorAll("header, .site-header, [role='banner'], .announcement-bar, .shopify-section-group-header-group").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top > -1) maxBottom = Math.max(maxBottom, rect.bottom);
      });
      root.style.setProperty("--sl-nav-height", `${maxBottom + 2}px`);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedId, setSelectedId] = useState(() => location.hash.slice(1) || null);
  const [mobileView, setMobileView] = useState("list");

  useEffect(() => {
    location.hash = selectedId ?? "";
  }, [selectedId]);

  // Sync URL hash to selectedId when the user navigates back/forward
  useEffect(() => {
    const onHash = () => setSelectedId(location.hash.slice(1) || null);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const storesWithDistance = useMemo(() => {
    return storesBase.map((s) => {
      if (!userLoc) return { ...s, distance_m: null };
      return { ...s, distance_m: haversineMeters(userLoc.lat, userLoc.lng, s.lat, s.lng) };
    });
  }, [storesBase, userLoc]);

  const countries = useMemo(() => uniqSorted(storesBase.map((s) => s.country)), [storesBase]);

  const cities = useMemo(() => {
    return uniqSorted(
      storesBase
        .filter((s) => (!selectedCountry ? true : s.country === selectedCountry))
        .map((s) => s.city)
    );
  }, [storesBase, selectedCountry]);

  // Clear city filter if the current city is no longer valid for the selected country
  useEffect(() => {
    if (selectedCity && !cities.includes(selectedCity)) setSelectedCity("");
  }, [cities, selectedCity]);

  const filteredStores = useMemo(() => {
    const list = storesWithDistance.filter((s) => {
      if (selectedCountry && s.country !== selectedCountry) return false;
      if (selectedCity && s.city !== selectedCity) return false;
      return true;
    });

    list.sort((a, b) => {
      const da = Number.isFinite(a.distance_m) ? a.distance_m : Number.POSITIVE_INFINITY;
      const db = Number.isFinite(b.distance_m) ? b.distance_m : Number.POSITIVE_INFINITY;
      if (da !== db) return da - db;
      return a.name.localeCompare(b.name);
    });

    return list;
  }, [storesWithDistance, selectedCountry, selectedCity]);

  const selectedStore = useMemo(() => {
    if (!selectedId) return null;
    return storesWithDistance.find((s) => s.id === selectedId) || null;
  }, [selectedId, storesWithDistance]);

  const styleUrl = root.dataset.mapStyleUrl || "";
  const storeMarkerUrl = root.dataset.storeMarker || "";
  const recenterIconUrl = root.dataset.recenterIcon || "";
  const defaultLat = parseFloat(root.dataset.defaultLat) || 35.6895;
  const defaultLng = parseFloat(root.dataset.defaultLng) || 139.6917;

  const mapContainerRef = useRef(null);
  const { mapRef, mapLoaded } = useMapLibre({ containerRef: mapContainerRef, styleUrl, defaultLat, defaultLng });
  const markersRef = useRef(new Map());
  const userMarkerRef = useRef(null);
  const hasFlewToUser = useRef(false);
  const filterInitRef = useRef(false);
  const panTriggeredFilterRef = useRef(false);

  const setFilterNearLocation = (lat, lng) => {
    if (storesBase.length === 0) return;
    const nearest = storesBase.reduce((best, s) => {
      const d = haversineMeters(lat, lng, s.lat, s.lng);
      return d < best.d ? { s, d } : best;
    }, { s: storesBase[0], d: Infinity }).s;
    panTriggeredFilterRef.current = true;
    setSelectedCountry(nearest.country);
    setSelectedCity(nearest.city);
  };

  const recenterToUser = () => {
    const map = mapRef.current;
    if (!map || !userLoc) return;
    map.flyTo({ center: [userLoc.lng, userLoc.lat], zoom: Math.max(map.getZoom(), 12), duration: 1000 });
    setFilterNearLocation(userLoc.lat, userLoc.lng);
  };

  // When the user changes a filter dropdown, fly/fitBounds to the matching stores
  useEffect(() => {
    if (!filterInitRef.current) { filterInitRef.current = true; return; }
    if (panTriggeredFilterRef.current) { panTriggeredFilterRef.current = false; return; }
    const map = mapRef.current;
    if (!map || !mapLoaded || filteredStores.length === 0) return;
    if (filteredStores.length === 1) {
      map.flyTo({ center: [filteredStores[0].lng, filteredStores[0].lat], zoom: 13, duration: 1200 });
      return;
    }
    const lngs = filteredStores.map((s) => s.lng);
    const lats = filteredStores.map((s) => s.lat);
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 80, duration: 1200, maxZoom: 13 }
    );
  }, [selectedCountry, selectedCity, mapLoaded]);

  // On user-initiated map pan/zoom, update filters to match visible stores in the viewport
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const handleMoveEnd = (e) => {
      if (!e.originalEvent) return;
      const bounds = map.getBounds();
      const visible = storesBase.filter((s) => bounds.contains([s.lng, s.lat]));
      panTriggeredFilterRef.current = true;
      if (visible.length === 0) {
        setSelectedCountry("");
        setSelectedCity("");
        return;
      }
      const countries = [...new Set(visible.map((s) => s.country))];
      if (countries.length > 1) {
        setSelectedCountry("");
        setSelectedCity("");
        return;
      }
      const cities = [...new Set(visible.map((s) => s.city))];
      setSelectedCountry(countries[0]);
      setSelectedCity(cities.length === 1 ? cities[0] : "");
    };

    map.on("moveend", handleMoveEnd);
    return () => { map.off("moveend", handleMoveEnd); };
  }, [mapLoaded, storesBase]);

  // Place/update the user dot marker; fly to it once on first geolocation fix
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !userLoc) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setLngLat([userLoc.lng, userLoc.lat]);
    } else {
      const el = document.createElement("div");
      el.className = "store-locator__user-dot";
      userMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([userLoc.lng, userLoc.lat])
        .addTo(map);
    }

    if (!hasFlewToUser.current && !selectedId) {
      hasFlewToUser.current = true;
      map.flyTo({ center: [userLoc.lng, userLoc.lat], zoom: 11, duration: 1800 });
      setFilterNearLocation(userLoc.lat, userLoc.lng);
    }
  }, [mapLoaded, userLoc, selectedId]);

  // Sync store markers (create missing, show/hide by filter) and pan to selected store
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    function ensureMarker(store) {
      if (markersRef.current.has(store.id)) return markersRef.current.get(store.id);

      const el = document.createElement("button");
      el.type = "button";
      el.setAttribute("aria-label", store.name);
      el.style.cursor = "pointer";
      el.style.padding = "0";
      el.style.border = "none";
      el.style.borderRadius = "50%";


      if (storeMarkerUrl) {
        el.style.width = "36px";
        el.style.height = "36px";
        const img = document.createElement("img");
        img.src = storeMarkerUrl;
        img.alt = "";
        img.style.width = "100%";
        img.style.height = "100%";
        img.style.objectFit = "contain";
        img.style.pointerEvents = "none";
        el.appendChild(img);
      } else {
        el.style.width = "18px";
        el.style.height = "18px";
        el.style.borderRadius = "50%";
        el.style.border = "2px solid rgba(255,255,255,0.8)";
        el.style.background = "#e46f2a";
        el.style.boxShadow = "0 2px 10px rgba(0,0,0,0.4)";
      }

      el.addEventListener("click", (e) => {
        e.preventDefault();
        setSelectedId(store.id);
      });

      const marker = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([store.lng, store.lat]);
      marker.addTo(map);
      markersRef.current.set(store.id, marker);
      return marker;
    }

    storesBase.forEach((s) => ensureMarker(s));

    const visible = new Set(filteredStores.map((s) => s.id));
    storesBase.forEach((s) => {
      const marker = markersRef.current.get(s.id);
      if (!marker) return;
      marker.getElement().style.display = visible.has(s.id) ? "block" : "none";
    });

    if (selectedStore) {
      map.easeTo({ center: [selectedStore.lng, selectedStore.lat], zoom: Math.max(map.getZoom(), 11) });
    }
  }, [mapLoaded, storesBase, filteredStores, selectedStore]);

  // Close the detail overlay when the user presses Escape
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") setSelectedId(null);
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const storeCountText = userLoc
    ? `${filteredStores.length} Filialen in Ihrer Nähe`
    : `${filteredStores.length} Filialen`;

  return html`
    <${Fragment}>
      <div class="store-locator__container" data-mobile-view=${mobileView}>
        <div class="store-locator__map" aria-label="Store map">
          <div class="store-locator__map-canvas" ref=${mapContainerRef}></div>
          <div class="store-locator__map-controls">
              ${userLoc ? html`
              <button
                type="button"
                class="store-locator__map-ctrl-btn"
                aria-label="Recenter map to your location"
                onClick=${recenterToUser}
              >
                <img src=${recenterIconUrl} alt="recenter" width="20" height="20" />
              </button>
            ` : null}
            <button type="button" class="store-locator__map-ctrl-btn" aria-label="Zoom in" onClick=${() => mapRef.current?.zoomIn()}>+</button>
            <button type="button" class="store-locator__map-ctrl-btn" aria-label="Zoom out" onClick=${() => mapRef.current?.zoomOut()}>−</button>
          </div>
        </div>

        <div class="store-locator__toolbar">
          <p class="store-locator__title">Filiale finden</p>
          <div class="store-locator__filters">
            <select
              class="store-locator__select"
              aria-label="Land / Region"
              value=${selectedCountry}
              onChange=${(e) => setSelectedCountry(e.target.value)}
            >
              <option value="">Land / Region</option>
              ${countries.map((c) => html`<option value=${c}>${c}</option>`)}
            </select>
            <select
              class="store-locator__select"
              aria-label="City"
              value=${selectedCity}
              onChange=${(e) => setSelectedCity(e.target.value)}
            >
              <option value="">Stadt</option>
              ${cities.map((c) => html`<option value=${c}>${c}</option>`)}
            </select>
          </div>
          <div class="store-locator__meta-row">
            <div class="store-locator__meta">${storeCountText}</div>
            <div class="store-locator__view-toggle">
              <button
                type="button"
                class=${`store-locator__view-btn${mobileView === "list" ? " store-locator__view-btn--active" : ""}`}
                onClick=${() => setMobileView("list")}
              >List</button>
              <button
                type="button"
                class=${`store-locator__view-btn${mobileView === "map" ? " store-locator__view-btn--active" : ""}`}
                onClick=${() => setMobileView("map")}
              >Map</button>
            </div>
          </div>
        </div>

        <aside class="store-locator__panel" aria-label="Store list">
          <div class="store-locator__list">
            ${filteredStores.map((s) => html`<${StoreCard} store=${s} onSelect=${setSelectedId} />`)}
          </div>
        </aside>
      </div>
      <${DetailOverlay} store=${selectedStore} onClose=${() => setSelectedId(null)} />
    <//>
  `;
}

function mount(root) {
  const appEl = root.querySelector("[data-store-locator-app]");
  if (!appEl) return;
  render(html`<${App} root=${root} />`, appEl);
}

export function boot() {
  document.querySelectorAll("[data-store-locator]").forEach((root) => {
    if (root.dataset.storeLocatorInitialized === "true") return;
    root.dataset.storeLocatorInitialized = "true";
    mount(root);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
